import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {environment} from '@environments/environment';
import {Subaccount} from './subaccounts.interface';
import {map, share} from 'rxjs/operators';
import {getDateFormatNoHours, getMomentDateString} from '@app/shared/_helpers/date';
import {skipModalError} from '@app/shared/_models/auth';
import {Storage} from '@app/shared/_interfaces/storage';
import * as R from 'ramda';

@Injectable({
  providedIn: 'root'
})
export class SubaccountsService {
  private http = inject(HttpClient);


  readonly apiUrl = environment.newApiUrl;
  private prefix = `${this.apiUrl}/subaccounts`;

  getActivePlan(id: number) {
    return this.http
      .get<Subaccount.Plan>(`${this.prefix}/${id}/active-plan`, {
        context: skipModalError([404])
      });
  }

  /*
   new API: /api/subaccounts/api/plans/{planId}
   {
     plan_expiration: string,
     smtp_limit: number | -1
   }
  */

  updateSmtpLimits(subaccountId: number, limit: number) {
    return this.http
      .post<Subaccount.Plan>(`${this.prefix}/${subaccountId}/updatesubaccountsmtplimit`, {limit});
  }

  activateSubAccount(subaccountId: number, active: boolean) {
    return this.http
      .post<Subaccount.ItemDetails>(`${this.prefix}/${subaccountId}/updatesubaccountstatus`, {active});
  }

  getSubAccountDetails(subaccountId: number) {
    return this.http
      .get<Subaccount.ItemDetails>(`${this.prefix}/${subaccountId}`);
  }

  getSubaccounts(
    params?: Partial<Subaccount.ListRequest>
  ) {

    params = R.pipe(
      R.ifElse(
        R.has('filterByIP'),
        R.pipe(
          R.assoc('filterByIP[]', R.prop('filterByIP', params)),
          R.dissoc('filterByIP'),
        ),
        R.identity
      ),
      R.dissoc('showFilters')
    )(params);


    return this.http
      .get<Subaccount.ListData>(`${this.prefix}/list`, {
        params,
        context: skipModalError([403])
      })
      .pipe(
        map<Subaccount.ListData, Subaccount.ListData>(
          data => ({
            ...data,
            results: data.results.map(subaccount => ({
              ...subaccount,
              last_used: subaccount?.last_used && getMomentDateString(
                subaccount.last_used, getDateFormatNoHours()
              ),
              plan_expiration: subaccount?.plan_expiration && getMomentDateString(
                subaccount.plan_expiration, getDateFormatNoHours()
              ),
              plan_usage: subaccount.limit !== -1 ? parseFloat((subaccount.sent * 100 / subaccount.limit).toFixed(2)) : 0
            }))
          })
        )
      );
  }

  getAgencySettings() {
    return this.http
      .get<Subaccount.AgencySettings>(
        `${this.prefix}/agency`
      );
  }

  getLogo() {
    return this.http
      .get<{ logoUrl: string }>(
        `${this.prefix}/logo`
      );
  }

  removeLogo() {
    return this.http
      .delete<{ message: string }>(
        `${this.prefix}/logo`
      );
  }

  uploadLogo(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<Storage.UploadResponse>(`${this.prefix}/logo`, formData);
  }

  updateAgencySettings(settings: Partial<Omit<Subaccount.AgencySettings, 'logoUrl'>>) {
    return this.http
      .patch<Subaccount.AgencySettings>(`${this.prefix}/agency`, settings);
  }

  login(email: string) {
    return this.http
      .post<{ auth: string }>(`${this.prefix}/authorize`, {email});
  }

  addSubAccount(requestData: Subaccount.AddSubaccountRequest) {
    return this.http
      .post<Subaccount.ItemDetails>(`${this.prefix}`, requestData)
      .pipe(
        share()
      );
  }

  editSubAccount(subAccountId: number, requestData: Subaccount.AddSubaccountRequest) {
    return this.http
      .patch<Subaccount.ItemDetails>(`${this.prefix}/${subAccountId}`, requestData)
      .pipe(
        share()
      );
  }


  checkIfAccountExists(email: string) {
    return this.http
      .get<{ result: boolean }>(`${this.prefix}/email-exists`, {
        params: {
          Email: email
        }
      });
  }

}
